import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import QuotationCard from './QuotationCard';
import AcceptanceForm from './AcceptanceForm';
import CompareView from './CompareView';

const API_BASE = '/api/public/quotations';

function getVersionDisplayName(quotation) {
  if (!quotation) return 'Quotation';
  const custom = String(quotation?.VersionLabel || '').trim();
  return custom || `v${quotation?.VersionNumber || ''}`;
}

function getVersionSubLabel(quotation) {
  const custom = String(quotation?.VersionLabel || '').trim();
  if (!custom) return '';
  return `v${quotation?.VersionNumber || ''}`;
}

export default function CustomerQuotePage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | error | main | accepting | accepted
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [acceptingQuotation, setAcceptingQuotation] = useState(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptedInfo, setAcceptedInfo] = useState(null);

  // Compare mode state
  const [compareMode, setCompareMode] = useState(false);
  const [compareIdA, setCompareIdA] = useState(null);
  const [compareIdB, setCompareIdB] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const projectNumber = data?.project?.Projectnummer;
    document.title = projectNumber ? `Quotation ${projectNumber}` : 'Quotation';
  }, [data?.project?.Projectnummer]);

  const fetchData = async () => {
    setState('loading');
    try {
      const res = await fetch(`${API_BASE}/${token}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErrorMessage(body.message || 'This link is no longer valid.');
        setState('error');
        return;
      }
      const json = await res.json();
      setData(json);
      // Auto-select first quotation
      if (json.quotations && json.quotations.length > 0) {
        setSelectedQuotationId(json.quotations[0].ID);
      }
      setState('main');
    } catch (err) {
      setErrorMessage('Unable to load quotation. Please check your connection and try again.');
      setState('error');
    }
  };

  const handleAccept = (quotation) => {
    setAcceptingQuotation(quotation);
    setState('accepting');
  };

  const handleSubmitAcceptance = async (payload) => {
    setAcceptLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body.message || 'Failed to submit acceptance');
        setAcceptLoading(false);
        return;
      }
      const result = await res.json();
      setAcceptedInfo(result.acceptance);
      setState('accepted');
    } catch (err) {
      alert('Network error. Please try again.');
    }
    setAcceptLoading(false);
  };

  const handleCancelAcceptance = () => {
    setAcceptingQuotation(null);
    setState('main');
  };

  const enterCompareMode = () => {
    const quotations = data?.quotations || [];
    setCompareIdA(quotations[0]?.ID || null);
    setCompareIdB(quotations[quotations.length - 1]?.ID || null);
    setCompareData(null);
    setCompareMode(true);
  };

  const exitCompareMode = () => {
    setCompareMode(false);
    setCompareData(null);
  };

  // Fetch comparison when both IDs are selected
  useEffect(() => {
    if (!compareMode || !compareIdA || !compareIdB || compareIdA === compareIdB) {
      if (compareMode && compareIdA === compareIdB) setCompareData(null);
      return;
    }
    let cancelled = false;
    setCompareLoading(true);
    fetch(`${API_BASE}/${token}/compare/${compareIdA}/${compareIdB}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load comparison'))))
      .then((json) => {
        if (!cancelled) setCompareData(json);
      })
      .catch(() => {
        if (!cancelled) setCompareData(null);
      })
      .finally(() => {
        if (!cancelled) setCompareLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [compareMode, compareIdA, compareIdB, token]);

  const selectedQuotation = data?.quotations?.find((q) => q.ID === selectedQuotationId);
  const pdfUrl = selectedQuotationId ? `${API_BASE}/${token}/pdf/${selectedQuotationId}` : null;

  const getCompareOptionFullLabel = (quotation) => {
    if (!quotation) return '';
    const base = getVersionDisplayName(quotation);
    const sub = getVersionSubLabel(quotation);
    return sub ? `${base} (${sub})` : base;
  };

  const getCompareOptionDisplayLabel = (quotation, maxLabelLength = 24) => {
    const fullLabel = getCompareOptionFullLabel(quotation);
    const displayName = getVersionDisplayName(quotation);
    if (displayName.length <= maxLabelLength) return fullLabel;
    const truncated = displayName.slice(0, Math.max(maxLabelLength - 1, 1)).trimEnd();
    const sub = getVersionSubLabel(quotation);
    return sub ? `${truncated}… (${sub})` : `${truncated}…`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>
      {/* Minimal top accent line */}
      <div style={{ height: '3px', background: '#1a1a1a' }} />

      {/* Loading */}
      {state === 'loading' && (
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                border: '3px solid #e5e5e5',
                borderTopColor: '#1a1a1a',
                borderRadius: '50%',
                margin: '0 auto 16px',
                animation: 'spin 0.8s linear infinite'
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            <div style={{ fontSize: '14px', color: '#999', letterSpacing: '0.02em' }}>Loading...</div>
          </div>
        </div>
      )}

      {/* Error / Invalid / Expired */}
      {state === 'error' && (
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '48px 40px', border: '1px solid #eee', maxWidth: '440px', margin: '0 auto' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  border: '2px solid #ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#999',
                  fontSize: '20px',
                  fontWeight: 300
                }}
              >
                ?
              </div>
              <h2 style={{ margin: '0 0 8px 0', color: '#1a1a1a', fontSize: '18px', fontWeight: 600 }}>Link unavailable</h2>
              <p style={{ color: '#777', fontSize: '14px', margin: 0, lineHeight: 1.6 }}>{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main View - Split Layout */}
      {state === 'main' && data && (
        <div style={{ display: 'flex', height: 'calc(100vh - 3px)' }} className="customer-quote-split">
          {/* Left Panel - Version List */}
          <div
            style={{
              width: '320px',
              flexShrink: 0,
              borderRight: '1px solid #eee',
              background: 'white',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            className="customer-quote-left"
          >
            {/* Project Header */}
            <div style={{ padding: '24px 20px 16px', borderBottom: '1px solid #f0f0f0' }}>
              {data.recipientName && <p style={{ margin: '0 0 8px 0', color: '#777', fontSize: '13px' }}>Hello {data.recipientName},</p>}
              <h1 style={{ margin: '0 0 4px 0', color: '#1a1a1a', fontSize: '18px', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                {data.project.Omschrijving || `Project ${data.project.Projectnummer}`}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <p style={{ margin: 0, color: '#999', fontSize: '12px', letterSpacing: '0.01em' }}>
                  {data.project.Projectnummer}
                  {data.project.KlantNaam ? `  /  ${data.project.KlantNaam}` : ''}
                </p>
                {data.quotations.length >= 2 &&
                  (compareMode ? (
                    <button
                      onClick={exitCompareMode}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 500,
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#555',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'border-color 0.15s, background 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#aaa';
                        e.target.style.background = '#fafafa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#ddd';
                        e.target.style.background = 'white';
                      }}
                    >
                      &larr; Preview
                    </button>
                  ) : (
                    <button
                      onClick={enterCompareMode}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 500,
                        border: '1px solid #d0d7f5',
                        borderRadius: '6px',
                        background: '#f0f4ff',
                        color: '#3b5bdb',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'border-color 0.15s, background 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#a0b0e8';
                        e.target.style.background = '#e4ebff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#d0d7f5';
                        e.target.style.background = '#f0f4ff';
                      }}
                    >
                      Compare
                    </button>
                  ))}
              </div>
            </div>

            {/* Scrollable Version List */}
            <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
              {/* Compare mode dropdowns */}
              {compareMode && (
                <div style={{ marginBottom: '12px', padding: '12px', background: '#f8f9fb', borderRadius: '10px', border: '1px solid #eee' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '8px', letterSpacing: '0.02em' }}>Compare</div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <select
                        value={compareIdA || ''}
                        onChange={(e) => setCompareIdA(Number(e.target.value))}
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '12px',
                          background: 'white',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {data.quotations.map((q) => (
                          <option key={q.ID} value={q.ID} title={getCompareOptionFullLabel(q)}>
                            {getCompareOptionDisplayLabel(q)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span style={{ fontSize: '12px', color: '#999', flexShrink: 0 }}>&rarr;</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <select
                        value={compareIdB || ''}
                        onChange={(e) => setCompareIdB(Number(e.target.value))}
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '12px',
                          background: 'white',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {data.quotations.map((q) => (
                          <option key={q.ID} value={q.ID} title={getCompareOptionFullLabel(q)}>
                            {getCompareOptionDisplayLabel(q)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {compareIdA === compareIdB && <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '6px' }}>Select two different versions</div>}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.quotations.map((q) => (
                  <QuotationCard
                    key={q.ID}
                    quotation={q}
                    token={token}
                    selected={!compareMode && q.ID === selectedQuotationId}
                    onSelect={() => {
                      if (!compareMode) setSelectedQuotationId(q.ID);
                    }}
                    onAccept={compareMode ? undefined : handleAccept}
                    hideAccept={compareMode}
                  />
                ))}
              </div>

              {data.quotations.length === 0 && (
                <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                  <p style={{ color: '#999', fontSize: '13px', margin: 0 }}>No quotations available for review at this time.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - PDF Preview or Compare View */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: compareMode ? '#fafafa' : '#f0f0f0' }} className="customer-quote-right">
            {compareMode ? (
              <CompareView
                areas={compareData?.areas || []}
                versionA={compareData?.versionA}
                versionB={compareData?.versionB}
                totalDelta={compareData?.totalDelta}
                totalAdded={compareData?.totalAdded}
                totalRemoved={compareData?.totalRemoved}
                totalChanged={compareData?.totalChanged}
                areaConfigs={data.quotations.find((q) => q.ID === compareIdB)?.areaConfigs || []}
                loading={compareLoading}
              />
            ) : pdfUrl ? (
              <iframe key={selectedQuotationId} src={pdfUrl} style={{ flex: 1, border: 'none', width: '100%' }} title={`Quotation ${getVersionDisplayName(selectedQuotation)}`} />
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p style={{ color: '#999', fontSize: '14px' }}>Select a quotation to preview</p>
              </div>
            )}
          </div>

          {/* Responsive styles */}
          <style>{`
            @media (max-width: 768px) {
              .customer-quote-split {
                flex-direction: column !important;
                height: auto !important;
                min-height: calc(100vh - 3px);
              }
              .customer-quote-left {
                width: 100% !important;
                border-right: none !important;
                border-bottom: 1px solid #eee;
                max-height: 40vh;
              }
              .customer-quote-right {
                min-height: 60vh;
              }
            }
          `}</style>
        </div>
      )}

      {/* Acceptance Form */}
      {state === 'accepting' && acceptingQuotation && (
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px 80px' }}>
          <button
            onClick={handleCancelAcceptance}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '20px',
              padding: '7px 14px',
              fontSize: '13px',
              fontWeight: 500,
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: 'white',
              color: '#555',
              cursor: 'pointer',
              transition: 'border-color 0.15s'
            }}
            onMouseEnter={(e) => (e.target.style.borderColor = '#aaa')}
            onMouseLeave={(e) => (e.target.style.borderColor = '#ddd')}
          >
            &larr; Back to quotes
          </button>
          <AcceptanceForm
            quotation={acceptingQuotation}
            optionalLines={acceptingQuotation.optionalLines || []}
            areaConfigs={acceptingQuotation.areaConfigs || []}
            onSubmit={handleSubmitAcceptance}
            onCancel={handleCancelAcceptance}
            loading={acceptLoading}
          />
        </div>
      )}

      {/* Accepted Confirmation */}
      {state === 'accepted' && (
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '48px 40px', border: '1px solid #eee', maxWidth: '440px', margin: '0 auto' }}>
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  background: '#1a1a1a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: 'white',
                  fontSize: '22px'
                }}
              >
                &#10003;
              </div>
              <h2 style={{ margin: '0 0 8px 0', color: '#1a1a1a', fontSize: '20px', fontWeight: 600 }}>Accepted</h2>
              <p style={{ color: '#666', fontSize: '14px', margin: '0 0 4px 0', lineHeight: 1.6 }}>
                Thank you{acceptedInfo?.SignatoryName ? `, ${acceptedInfo.SignatoryName}` : ''}. Your acceptance has been recorded.
              </p>
              <p style={{ color: '#999', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>We'll be in touch shortly.</p>
              <button
                onClick={() => {
                  setAcceptedInfo(null);
                  fetchData();
                }}
                style={{
                  marginTop: '28px',
                  padding: '9px 22px',
                  fontSize: '13px',
                  fontWeight: 500,
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#555',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s'
                }}
                onMouseEnter={(e) => (e.target.style.borderColor = '#aaa')}
                onMouseLeave={(e) => (e.target.style.borderColor = '#ddd')}
              >
                Back to overview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
